/**
 * Sentence splitting for the interview audio path.
 *
 * The interviewer's reply is streamed, split into sentences, and each sentence
 * is synthesized to speech separately so playback can start before the whole
 * answer has arrived. The naive version of this — scan for `[.!?]+`, keep
 * fragments longer than N characters — loses text in two ways at once:
 *
 *   1. It splits inside decimals and abbreviations. "I have 3.5 years" became
 *      "I have 3." + "5 years", so the interviewer heard a different number
 *      than the candidate said.
 *   2. It DROPPED fragments below the length threshold while still advancing
 *      past them, so those characters never reached speech or the transcript.
 *      "I worked in the U.S.A. last year" lost "S.A." outright.
 *
 * This module splits only on terminators that are actually followed by
 * whitespace or end-of-buffer (which leaves decimals intact), skips a list of
 * common abbreviations, and never discards text.
 */

/** Lowercased tokens that end in "." without ending a sentence. */
const ABBREVIATIONS = [
  'mr',
  'mrs',
  'ms',
  'dr',
  'prof',
  'sr',
  'jr',
  'st',
  'vs',
  'etc',
  'e.g',
  'i.e',
  'inc',
  'ltd',
  'co',
  'approx',
  'fig',
  'al',
  'dept',
  'est',
  'min',
  'max',
  'no',
];

/**
 * A sentence terminator must be followed by whitespace or the end of the
 * buffer. That single constraint is what keeps "3.5" and "Node.js" whole.
 */
const TERMINATOR = /[.!?]+(?=\s|$)/g;

const abbreviationSet = new Set(ABBREVIATIONS);

/**
 * Split off every complete sentence in `buffer`.
 *
 * @param {string} buffer - accumulated text, possibly ending mid-sentence
 * @returns {{sentences: string[], rest: string}} complete sentences, and the
 *   trailing incomplete text that should stay buffered for the next chunk.
 *   Concatenating `sentences` and `rest` preserves every character of input
 *   apart from the whitespace between sentences.
 */
export function splitSentences(buffer) {
  const text = buffer || '';
  const sentences = [];
  let lastIndex = 0;
  let match;

  TERMINATOR.lastIndex = 0;
  while ((match = TERMINATOR.exec(text)) !== null) {
    const end = match.index + match[0].length;
    const candidate = text.slice(lastIndex, end);

    // Is the terminator just an abbreviation's full stop? If so, keep scanning
    // without closing the sentence here.
    const lastToken = candidate.trim().split(/\s+/).pop() || '';
    const bare = lastToken.replace(/[.!?]+$/, '').toLowerCase();
    if (abbreviationSet.has(bare)) continue;

    const trimmed = candidate.trim();
    // Never drop a fragment: a short one is still something the candidate or
    // interviewer said.
    if (trimmed) sentences.push(trimmed);
    lastIndex = end;
  }

  return { sentences, rest: text.slice(lastIndex) };
}

export default splitSentences;
